import {
  Box,
  FirstPageRoundedIcon,
  Flex,
  IconButton,
  LastPageRoundedIcon,
  NavigateBeforeRoundedIcon,
  NavigateNextRoundedIcon,
  Selection,
  Text,
  type OptionSelectionProps,
} from '@shared/components';

interface SearchPaginationProps {
  total: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
}

const ROWS_PER_PAGE_OPTIONS: OptionSelectionProps[] = [
  { label: '20', value: 20 },
  { label: '50', value: 50 },
  { label: '100', value: 100 },
];

export default function SearchPagination({
  total,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
}: Readonly<SearchPaginationProps>) {
  const totalPages = Math.ceil(total / rowsPerPage);
  const from = total === 0 ? 0 : page * rowsPerPage + 1;
  const to = Math.min((page + 1) * rowsPerPage, total);
  const isFirstPage = page === 0;
  const isLastPage = page >= totalPages - 1;

  return (
    <Flex sx={{ justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
      <Flex sx={(theme) => ({ alignItems: 'center', gap: theme.spacing(1), flexShrink: 0 })}>
        <Text variant='body2' sx={{ whiteSpace: 'nowrap' }}>
          แสดง
        </Text>
        <Selection
          size='small'
          value={rowsPerPage}
          onChange={(e) => onRowsPerPageChange(Number(e.target.value))}
          options={ROWS_PER_PAGE_OPTIONS}
          sx={{
            '& .MuiInputBase-root': { height: 32 },
            '& .MuiSelect-select': { py: '4px', px: '8px', fontSize: 12 },
          }}
        />
        <Text variant='body2' sx={{ whiteSpace: 'nowrap' }}>
          รายการต่อหน้า
        </Text>
      </Flex>

      <Flex sx={(theme) => ({ alignItems: 'center', gap: theme.spacing(0.5) })}>
        <Text variant='body2' sx={(theme) => ({ mr: theme.spacing(1) })}>
          {from}-{to} จาก {total}
        </Text>
        <IconButton
          size='small'
          aria-label='หน้าแรก'
          onClick={() => onPageChange(0)}
          disabled={isFirstPage}
        >
          <FirstPageRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <IconButton
          size='small'
          aria-label='หน้าก่อนหน้า'
          onClick={() => onPageChange(page - 1)}
          disabled={isFirstPage}
        >
          <NavigateBeforeRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <Box
          sx={(theme) => ({
            borderRadius: theme.shape.rounded(1),
            minWidth: 32,
            p: theme.spacing(1),
            textAlign: 'center',
            backgroundColor: theme.palette.grey[100],
          })}
        >
          <Text variant='body2Semibold' sx={(theme) => ({ color: theme.palette.grey[700] })}>
            {page + 1}
          </Text>
        </Box>
        <IconButton
          size='small'
          aria-label='หน้าถัดไป'
          onClick={() => onPageChange(page + 1)}
          disabled={isLastPage}
        >
          <NavigateNextRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <IconButton
          size='small'
          aria-label='หน้าสุดท้าย'
          onClick={() => onPageChange(totalPages - 1)}
          disabled={isLastPage}
        >
          <LastPageRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Flex>
    </Flex>
  );
}
